export default {
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    save: 'Save',
    cancel: 'Cancel',
    verified: 'Verified',
    viewDetails: 'View details',
    back: 'Back',
    next: 'Next',
  },
  nav: {
    marketplace: 'Marketplace',
    restaurants: 'For Restaurants',
    suppliers: 'For Suppliers',
    login: 'Log in',
    signup: 'Get started',
    dashboard: 'Dashboard',
    logout: 'Log out',
  },
  footer: {
    company: 'Company',
    support: 'Support',
    contact: 'CONTACT',
    about: 'About Foodrolla',
    mission: 'Our Mission',
    restaurants: 'For Restaurants',
    suppliers: 'For Suppliers',
    privacy: 'Privacy & Terms',
    faq: 'Frequently Asked Questions',
    contactText: 'Questions about purchasing or partnerships?',
    description: 'Dutch B2B food marketplace connecting businesses directly with local producers and artisan farms.',
    tagline: 'Designed for the finest kitchens.'
  },
  forRestaurants: {
    hero: {
      title: 'Discover ingredients that set your kitchen apart.',
      subtitle: 'Foodrolla helps restaurants, hotels, caterers and specialty food businesses connect directly with local producers and artisan suppliers.',
      primary: 'Explore Marketplace',
      secondary: 'Create Account'
    },
    why: {
      title: 'Why Foodrolla?',
      items: [
        { title: 'Direct Contact', desc: 'Buy directly from producers without unnecessary middlemen.' },
        { title: 'Fewer Intermediaries', desc: 'A shorter supply chain means fresher products and better margins.' },
        { title: 'Greater Transparency', desc: 'Full insight into the origin and production methods of your ingredients.' },
        { title: 'Discover Suppliers', desc: 'Easily find new, unique producers from different European regions.' },
        { title: 'Exceptional Quality', desc: 'Find ingredients that meet the highest professional standards.' },
        { title: 'Efficient Sourcing', desc: 'Streamline your procurement process through one unified digital platform.' }
      ]
    },
    how: {
      title: 'How does it work?',
      step1: { title: 'Search suppliers', desc: 'Discover thousands of verified local producers and regional wholesalers.' },
      step2: { title: 'Compare products', desc: 'Browse curated collections and compare specifications and pricing.' },
      step3: { title: 'Place orders', desc: 'Contact producers directly or place orders through the platform.' },
      step4: { title: 'Build partnerships', desc: 'Establish reliable, long-term relationships with your artisan partners.' }
    },
    stats: {
      title: 'Why restaurants choose Foodrolla',
      producers: { title: 'Local producers', desc: 'Direct access to regional excellence.' },
      ingredients: { title: 'Premium ingredients', desc: 'Hand-picked for professional kitchens.' },
      sourcing: { title: 'Transparent sourcing', desc: 'Know exactly where your food comes from.' },
      suppliers: { title: 'Professional suppliers', desc: 'Reliable partners for high-end gastronomy.' }
    },
    future: {
      tag: 'Coming soon',
      dashboard: 'Restaurant dashboard',
      favourites: 'Favourite suppliers',
      history: 'Order history',
      analytics: 'Analytics',
      notifications: 'Availability notifications',
      lists: 'Saved product lists'
    },
    benefits: {
      title: 'Frequent advantages',
      items: [
        { title: 'Direct purchasing', icon: 'FiShoppingBag' },
        { title: 'Local suppliers', icon: 'FiMapPin' },
        { title: 'Discover new products', icon: 'FiSearch' },
        { title: 'Less administration', icon: 'FiLayers' },
        { title: 'Better collaboration', icon: 'FiUsers' },
        { title: 'Save time', icon: 'FiClock' }
      ]
    },
    cta: {
      title: 'Ready to discover new suppliers?',
      subtitle: 'See how Foodrolla can simplify your purchasing process.',
      button: 'Explore Marketplace'
    }
  },
  faq: {
    title: 'Frequently Asked Questions',
    intro: 'Have questions about Foodrolla?',
    subtitle: "Here you'll find answers to the most common questions from restaurants, suppliers and other business users.",
    contactTitle: 'Still have a question?',
    contactSubtitle: 'Feel free to contact us.',
    sections: {
      general: {
        title: 'General',
        questions: {
          what: { q: 'What is Foodrolla?', a: 'Foodrolla is a Dutch B2B marketplace connecting professional kitchens directly with local producers and artisan farms. We provide the digital infrastructure for transparent sourcing, direct trade and efficient logistics.' },
          who: { q: 'Who is Foodrolla for?', a: 'Our platform is designed for professional food businesses, including restaurants, hotels, artisan bakeries, and high-end caterers, as well as dedicated food producers and regional wholesalers.' },
          countries: { q: 'Which countries does Foodrolla operate in?', a: 'Foodrolla is currently launching in the Netherlands. We have the ambition to gradually expand our network to other European culinary regions in the near future.' },
          free: { q: 'Is Foodrolla free to use?', a: 'Registration and marketplace browsing are free for both buyers and suppliers. Foodrolla charges a platform fee on successful transactions to cover payment processing, logistics coordination, and administrative support.' },
          account: { q: 'How do I create an account?', a: 'You can register by clicking "Get started" in the navigation menu. Buyers must provide valid professional credentials, while suppliers undergo a manual verification process to ensure quality standards.' }
        }
      },
      restaurants: {
        title: 'For Restaurants',
        questions: {
          search: { q: 'How do I search for suppliers?', a: 'You can use the Marketplace search bar to find specific ingredients, producers, or regions. Our filters allow you to browse by category, such as Produce, Dairy, or Coastal Catch.' },
          contact: { q: 'Can I contact suppliers directly?', a: 'Yes, direct communication is a core part of Foodrolla. Once you have an account, you can reach out to producers through their profile to discuss specific needs or seasonal availability.' },
          order: { q: 'How do I place an order?', a: 'Simply add products from any producer to your order and proceed to checkout. The platform facilitates the transaction and notifies the producer immediately.' },
          multiple: { q: 'Can I order from multiple suppliers?', a: 'Yes. You can source ingredients from dozens of different producers in a single session. Foodrolla consolidates these into a unified procurement experience.' },
          payments: { q: 'How do payments work?', a: 'Payments are handled securely through our platform. We support B2B payment terms, including SEPA direct debits and credit cards, providing a professional financial flow.' },
          logistics: { q: 'Will Foodrolla handle logistics?', a: 'Foodrolla coordinates consolidated logistics to ensure that your products arrive in one temperature-controlled delivery, maintaining maximum freshness from the source to your kitchen.' },
          favourites: { q: 'Can I save favourite suppliers?', a: 'Yes, this feature is currently under development and will allow you to build a curated list of your preferred artisan partners for faster re-ordering.' }
        }
      },
      suppliers: {
        title: 'For Suppliers',
        questions: {
          become: { q: 'How do I become a supplier?', a: 'Start by creating a supplier account. Our team will review your application and production standards to ensure a fit with our premium marketplace community.' },
          profile: { q: 'How do I create my supplier profile?', a: 'Once verified, you can access your Supplier Dashboard to upload your logo, describe your farm’s story, and showcase your production methods.' },
          add: { q: 'How do I add products?', a: 'Through the Product Management section of your dashboard, you can list ingredients with descriptions, origin details, and professional imagery.' },
          manage: { q: 'Can I manage my own catalogue?', a: 'Absolutely. You have full control over your listings, including the ability to activate or deactivate products based on seasonal availability.' },
          prices: { q: 'Can I update prices myself?', a: 'Yes. You can adjust your B2B pricing and unit sizes at any time through your dashboard to reflect market changes or volume discounts.' },
          find: { q: 'How do restaurants find my products?', a: 'Your products appear in our central marketplace and on your dedicated producer profile, where they are discoverable by thousands of professional buyers.' }
        }
      },
      payments: {
        title: 'Payments',
        questions: {
          processed: { q: 'How are payments processed?', a: 'We use industry-leading, PCI-compliant payment providers to handle all financial data, ensuring that every transaction is encrypted and secure.' },
          receive: { q: 'Does Foodrolla receive the payment?', a: 'Foodrolla facilitates the transfer of funds. We act as a secure intermediary, ensuring the producer is paid while providing the buyer with professional B2B payment terms.' },
          invoice: { q: 'Who invoices the customer?', a: 'Foodrolla provides a consolidated, legally compliant B2B invoice for the buyer, simplifying accounting for kitchens that source from multiple producers.' },
          fail: { q: 'What happens if a payment fails?', a: 'Our system will notify you immediately. We provide automated retry mechanisms and support to resolve any administrative billing issues quickly.' }
        }
      },
      delivery: {
        title: 'Delivery',
        questions: {
          who: { q: 'Who delivers the products?', a: 'Deliveries are handled by our network of specialized temperature-controlled logistics partners to ensure the highest standards of food safety.' },
          own: { q: 'Can suppliers arrange their own transport?', a: 'While we encourage using our consolidated network for efficiency, specific arrangements can be discussed for large-scale or specialized regional deliveries.' },
          collect: { q: 'Can restaurants collect orders themselves?', a: 'This feature is not currently available as we focus on providing a seamless, reliable delivery service directly to your establishment’s door.' },
          responsible: { q: 'Who is responsible for shipping?', a: 'Foodrolla coordinates the shipping process, but producers remain responsible for the quality and safe packaging of their products until they reach our hub.' }
        }
      },
      security: {
        title: 'Privacy & Security',
        questions: {
          protected: { q: 'How is my data protected?', a: 'We implement robust technical measures, including TLS encryption and secure data centers within the EEA, to protect your business and personal information.' },
          view: { q: 'Who can view my company information?', a: 'Public profile information is visible to marketplace users. Sensitive business and financial data are strictly protected and only accessible to authorized personnel.' },
          privacy: { q: 'How does Foodrolla handle privacy?', a: 'We are fully GDPR compliant. Our privacy practices are designed to respect your data rights and ensure transparency in how we handle information.' }
        }
      },
      roadmap: {
        title: 'Roadmap',
        questions: {
          features: { q: 'Which features are planned?', a: 'Planned for a future release are advanced seasonal forecasting tools, automated re-ordering, and deeper logistics integration for international trade.' },
          apps: { q: 'Will there be mobile apps?', a: 'Foodrolla is currently fully focused on developing a powerful web application. Our priority is to first fully build out and optimize the platform for suppliers and hospitality businesses. A mobile app is not on the roadmap at this time. Should this change in the future, we will communicate it through our website.' },
          analytics: { q: 'Will analytics become available?', a: 'Yes. We are building professional insight dashboards for suppliers to help them understand demand trends and optimize their production.' },
          languages: { q: 'Will multiple languages be supported?', a: 'Foodrolla is built to be multilingual. We currently support Dutch and English, with more European languages planned for future expansion.' }
        }
      }
    }
  },
  about: {
    title: 'About Foodrolla',
    subtitle: 'Connecting the finest kitchens with local excellence.',
    who: {
      title: 'Who we are',
      content: `Foodrolla is a premium Dutch B2B marketplace designed to bridge the gap between high-end gastronomy and dedicated food producers. Based in Amsterdam, the Netherlands, we serve as a central hub where restaurants, hotels, artisan bakeries, and specialty caterers can discover and source exceptional ingredients directly from the source. Our platform is built on the belief that the best kitchens deserve the best ingredients, delivered with absolute transparency and efficiency. While we are currently focused on the Dutch market, our ambition is to gradually expand our network to connect artisan producers and professional kitchens across the entire European continent.`
    },
    why: {
      title: 'Why Foodrolla exists',
      content: `The traditional food supply chain is often fragmented, involving numerous intermediaries that add cost and complexity while reducing transparency and freshness. For professional chefs, discovering small-scale artisan producers can be time-consuming, and for producers, reaching the right business partners is often a logistical challenge. Foodrolla exists to simplify this ecosystem. We provide a digital infrastructure that allows for direct relationships, ensuring that quality and provenance are never lost in transit. By shortening the supply chain, we help businesses focus on what they do best: creating exceptional culinary experiences.`
    },
    what: {
      title: 'What Foodrolla offers',
      content: `We offer a comprehensive suite of tools designed for the modern B2B food trade. Our marketplace allows buyers to discover regional excellence through a curated interface, while suppliers gain a professional digital presence to showcase their craft. Beyond simple discovery, Foodrolla provides professional supplier dashboards, real-time product management, and direct purchasing capabilities. Our future roadmap includes advanced order management and logistical coordination to further streamline the daily operations of professional kitchens and farms alike.`
    },
    values: {
      title: 'Our values',
      content: `At the core of Foodrolla are four fundamental pillars:\n\n• Quality: We exclusively partner with producers who demonstrate exceptional craftsmanship and high standards.\n\n• Transparency: Every product has a story, and we ensure that origin, methods, and pricing are clear for all parties.\n\n• Sustainability: By promoting local and direct sourcing, we aim to reduce the environmental impact of food logistics.\n\n• Relationships: We believe in long-term partnerships built on mutual trust and professional integrity.`
    },
    vision: {
      title: 'Future vision',
      content: `Our vision is to become the leading European B2B marketplace that empowers the connection between gastronomy and production. We are building a future where a chef in Paris can source artisan cheese from a Dutch farm, or a Belgian bakery can discover heritage grains from a regional miller, all through one unified and reliable platform. We are moving step-by-step, ensuring that every expansion maintains the high quality and personal touch that Foodrolla was founded upon.`
    }
  },
  mission: {
    title: 'Our Mission',
    subtitle: 'Redefining the journey from soil to service.',
    intro: {
      title: 'The Mission',
      content: `Our mission is to empower the professional food industry by creating a transparent, efficient, and direct marketplace for the finest ingredients. We believe that technology should serve gastronomy, not complicate it. By providing the tools for direct trade, we enable chefs and producers to build meaningful, sustainable relationships that reflect the quality of the products they handle.`
    },
    pillars: {
      producers: {
        title: 'Supporting local producers',
        content: `Small-scale and artisan producers are the backbone of European food culture. We are committed to giving these masters of their craft the digital visibility and professional tools they need to thrive in a modern B2B landscape, ensuring their legacy continues to reach the tables of the best establishments.`
      },
      discovery: {
        title: 'Exceptional discovery',
        content: `Finding the perfect ingredient shouldn't be a matter of luck. We curate our marketplace to ensure that professional buyers can easily discover regional excellence, seasonal highlights, and unique products that set their menus apart from the rest.`
      },
      intermediaries: {
        title: 'Reducing complexity',
        content: `Every unnecessary step in a supply chain is a loss of value and freshness. Our mission is to remove these hurdles, allowing for a more direct path from the producer's hand to the chef's knife, resulting in better margins for businesses and better quality for the final guest.`
      },
      technology: {
        title: 'Technology for gastronomy',
        content: `We build tools that understand the rhythm of a kitchen and the seasons of a farm. Our technology is designed to be a silent partner—reliable, intuitive, and focused on facilitating the real work that happens in the field and the kitchen.`
      }
    },
    closing: `Foodrolla is more than a platform; it is a commitment to the future of European gastronomy. We are here to ensure that the finest ingredients are sourced with the respect and efficiency they deserve.`
  },
  forSuppliers: {
    title: 'For Suppliers',
    subtitle: 'Grow your business with the best kitchens in Europe.',
    whyJoin: {
      title: 'Why join Foodrolla?',
      content: `Foodrolla provides your business with a professional digital gateway to the most prestigious kitchens and specialty food businesses. We handle the discovery and digital infrastructure so you can focus on your production. By joining our network, you gain access to a curated audience of professional buyers who value quality and provenance above all else. Our platform is designed to respect your brand, your pricing, and your story.`
    },
    benefits: {
      title: 'Key Benefits',
      content: `• Professional Online Presence: A dedicated profile to showcase your farm, methods, and products.\n• Direct Reach: Connect directly with restaurants, hotels, and caterers seeking premium ingredients.\n• Product Management: Easily update your catalog, pricing, and stock levels in real-time.\n• Order Streamlining: Receive inquiries and orders through a unified, professional interface.\n• Reputation Building: Build trust through our verified partner system and transparent reviews.\n• Future Insights: Access to data on market trends and customer preferences to help grow your business.`
    },
    onboarding: {
      title: 'How it works',
      step1: { title: 'Step 1: Create your account', content: 'Register your business on Foodrolla and gain access to the Supplier Dashboard.' },
      step2: { title: 'Step 2: Complete your profile', content: 'Tell your story. Upload images and describe your production methods and values.' },
      step3: { title: 'Step 3: Add products', content: 'List your ingredients with detailed information, pricing, and seasonal availability.' },
      step4: { title: 'Step 4: Publish your catalogue', content: 'Once reviewed for quality, your products will become visible to our network of professional buyers.' },
      step5: { title: 'Step 5: Start receiving orders', content: 'Receive inquiries and orders directly from chefs and procurement managers.' }
    },
    cta: {
      title: 'Ready to showcase your craft?',
      button: 'Become a Supplier'
    }
  },
  privacy: {
    title: 'Privacy & Terms',
    intro: 'LAST UPDATED: JULY 1, 2026',
    sections: {
      privacyStatement: {
        title: 'Privacy Statement',
        content: `Foodrolla ("we", "us", or "our"), based in Amsterdam, the Netherlands, is committed to protecting the privacy and security of our users' data. This Privacy Statement explains how we collect, use, and safeguard information within our B2B marketplace ecosystem. As a specialized platform connecting professional kitchens with artisan producers, we prioritize the integrity of business data and the privacy of the individuals who represent these establishments. By accessing our platform, you acknowledge the practices described in this comprehensive policy.`
      },
      personalInfo: {
        title: 'Personal Information',
        content: `We collect personal information that you voluntarily provide when registering an account, subscribing to our newsletter, or communicating with our support team. This typically includes your full name, professional email address, and direct phone number. In the context of professional procurement, we treat this data with the highest level of confidentiality, ensuring it is only accessible to authorized personnel and necessary partners involved in your transactions.`
      },
      businessInfo: {
        title: 'Business Information',
        content: `To maintain the professional nature of our marketplace, we require detailed business information. This includes company legal names, registered addresses, and professional credentials. For producers, we also collect information regarding farm locations, production methods, and certification status. This data is essential for building trust and ensuring the high standards expected by our restaurant partners.`
      },
      accountRegistration: {
        title: 'Account Registration',
        content: `Creating an account on Foodrolla requires a multi-step verification process. Users must provide valid professional credentials and undergo a screening process to ensure they are legitimate B2B entities. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Any unauthorized use must be reported to Foodrolla immediately to prevent potential security breaches.`
      },
      supplierVerification: {
        title: 'Supplier Verification',
        content: `Quality is the cornerstone of Foodrolla. Every supplier undergoes a rigorous manual verification process. This includes reviewing food safety certifications, production capacity, and ethical farming practices. We reserve the right to request additional documentation or conduct site visits to verify the information provided. Failure to maintain these standards may result in temporary suspension or permanent removal from the marketplace.`
      },
      restaurantAccounts: {
        title: 'Restaurant & Professional Accounts',
        content: `Buyer accounts are reserved for professional establishments, including restaurants, hotels, artisan bakeries, and high-end caterers. During registration, buyers must demonstrate their professional status through valid business registration. These accounts grant access to B2B pricing, volume discounts, and consolidated logistics services that are tailored to the demanding needs of professional kitchens.`
      },
      orders: {
        title: 'Orders',
        content: `Orders placed through Foodrolla constitute a binding agreement between the buyer and the producer. Foodrolla acts as the facilitating platform, providing the interface for selection, communication, and logistical coordination. Producers are responsible for updating stock levels in real-time. In the rare event of stock discrepancies, producers must communicate immediately with the buyer to offer suitable alternatives or adjustments.`
      },
      payments: {
        title: 'Payments',
        content: `Foodrolla facilitates secure B2B payments. We support various payment methods, including SEPA direct debits, credit cards, and traditional bank transfers for established accounts with approved credit limits. Our payment system is designed to handle the complexities of B2B transactions, including split-payments for orders involving multiple producers and automated reconciliation for accounting efficiency.`
      },
      paymentProviders: {
        title: 'Payment Providers',
        content: `We partner with industry-leading, PCI-compliant payment processors such as Stripe and Adyen to handle all financial data. Foodrolla does not store sensitive credit card information on its own servers. These providers operate under strict international security standards, ensuring that every transaction is encrypted and protected against fraudulent activity.`
      },
      vatInformation: {
        title: 'VAT Information',
        content: `As a B2B marketplace, VAT handling is a critical component of our service. For intra-community transactions within the EU, we apply the appropriate reverse-charge mechanisms based on valid VAT identification numbers. Users are responsible for ensuring their VAT information is current and accurate. Incorrect VAT data may lead to billing adjustments and potential administrative fees.`
      },
      billing: {
        title: 'Billing & Invoicing',
        content: `Foodrolla provides consolidated invoicing for buyers, simplifying the accounting process for kitchens that source from multiple producers. You will receive a single, legally compliant digital invoice for your consolidated delivery. Producers receive individual credit notes and statements detailing their sales, commissions, and logistical fees, all accessible through the Supplier Dashboard.`
      },
      cookies: {
        title: 'Cookies',
        content: `We use cookies to enhance your experience on our platform. Essential cookies are required for core functionality, such as maintaining your login session and managing your shopping cart. Preference cookies allow the platform to remember your language choices and regional settings, providing a more personalized and efficient procurement experience every time you return.`
      },
      analytics: {
        title: 'Analytics',
        content: `To improve our marketplace, we use anonymized analytics tools to understand how users interact with our site. This helps us optimize search results, improve navigation, and identify which regions or product categories require more attention. We do not use analytics to track individual users for advertising purposes across other websites.`
      },
      security: {
        title: 'Security',
        content: `We implement robust technical and organizational measures to protect your data. This includes TLS encryption for all data in transit, multi-factor authentication for sensitive account actions, and regular security audits. Our infrastructure is hosted in secure, ISO-certified data centers with 24/7 monitoring and physical security protocols.`
      },
      dataStorage: {
        title: 'Data Storage',
        content: `Your data is stored primarily on servers located within the European Economic Area (EEA). We select our hosting partners based on their ability to provide high availability and strict adherence to European data protection standards. Regular backups are performed to ensure data resilience and business continuity for our partners.`
      },
      gdprCompliance: {
        title: 'GDPR Compliance',
        content: `Foodrolla is fully compliant with the General Data Protection Regulation (GDPR). We act as a data controller for the information we collect to manage your account and as a data processor for certain transaction-related information. We have established clear internal procedures to handle data protection inquiries and ensure that privacy-by-design is a fundamental part of our development process.`
      },
      userRights: {
        title: 'User Rights',
        content: `Under GDPR, you have the right to access your data, request rectification of inaccuracies, and ask for the deletion of your personal information (the 'right to be forgotten'). You may also object to certain types of processing or request that your data be provided in a portable format. All such requests can be initiated by contacting our Data Protection Officer.`
      },
      dataRetention: {
        title: 'Data Retention',
        content: `We retain your information for as long as your account is active or as needed to provide you with our services. Financial records, including invoices and transaction history, are retained for the legally required period under applicable laws (typically 7 years). Anonymized data may be kept longer for historical analysis and platform improvement purposes.`
      },
      internationalTransfers: {
        title: 'International Transfers',
        content: `While we prioritize EEA-based storage, some service providers may operate outside the EEA. In such cases, we ensure that appropriate safeguards, such as Standard Contractual Clauses (SCCs), are in place to project your data. We regularly review these partnerships to ensure they meet the evolving standards of international data privacy law.`
      },
      marketingCommunications: {
        title: 'Marketing Communications',
        content: `We may send you updates about new producers, seasonal products, or platform enhancements. You have full control over these communications and can opt-out at any time through your account settings or the 'unsubscribe' link in our emails. Transactional emails, such as order confirmations and security alerts, are essential and cannot be opted out of.`
      },
      intellectualProperty: {
        title: 'Intellectual Property',
        content: `All content on the Foodrolla platform, including text, graphics, logos, and software code, is the property of Foodrolla or its content suppliers and is protected by international copyright laws. Users are granted a limited, non-exclusive license to access the platform for professional procurement purposes. Any unauthorized reproduction or distribution is strictly prohibited.`
      },
      platformRules: {
        title: 'Platform Rules',
        content: `Users must interact with the platform in a professional and lawful manner. Prohibited activities include attempting to circumvent our fee structure, harassing other users, or uploading malicious code. We maintain a zero-tolerance policy for fraudulent listings or misrepresentation of product quality. Violations of these rules may lead to immediate account termination.`
      },
      supplierTerms: {
        title: 'Supplier Terms',
        content: `Suppliers agree to provide accurate descriptions, maintain updated pricing, and fulfill orders according to our quality charter. You are responsible for the freshness and safety of the products until they reach our consolidated logistics hub. Foodrolla charges a platform fee on successful transactions, which covers marketing, payment processing, and administrative support.`
      },
      buyerResponsibilities: {
        title: 'Buyer Responsibilities',
        content: `Buyers are responsible for inspecting products upon delivery and reporting any issues within the specified timeframe (usually 2-4 hours for fresh produce). Accurate delivery information and ensuring a representative is available to receive the temperature-controlled shipment are critical responsibilities of the buyer.`
      },
      productListings: {
        title: 'Product Listings',
        content: `Producers must ensure that all listings include relevant information regarding origin, allergens, and certifications. Images must be representative of the actual product being sold. Foodrolla reserves the right to edit or remove listings that do not meet our aesthetic or informational standards.`
      },
      reviews: {
        title: 'Reviews',
        content: `Our marketplace features a transparent review system where buyers can rate their experience with specific producers and products. Reviews must be based on actual transactions and remain professional in tone. We moderate reviews to prevent abuse, but we do not remove negative feedback that is constructive and based on factual experience.`
      },
      liability: {
        title: 'Liability',
        content: `Foodrolla acts as an intermediary. While we vet all producers, the ultimate legal responsibility for product quality and safety lies with the producer. Foodrolla's liability is limited to the functionality of the platform and the coordination of the services described herein, as permitted by law.`
      },
      disputes: {
        title: 'Disputes',
        content: `Foodrolla acts solely as a facilitator and marketplace platform. Any sales agreement is concluded directly between the Buyer and the Supplier. The Supplier remains fully responsible for its own products, pricing, deliveries, and warranties. In the event of any issues or disagreements, Buyers should first contact the Supplier directly to seek an amicable resolution. While Foodrolla may provide reasonable assistance where appropriate to facilitate communication, Foodrolla is not a party to the sales agreement. Foodrolla does not act as a mediator or arbitrator in commercial disputes and shall not be held liable for any contractual disagreements or claims arising between users of the platform.`
      },
      governingLaw: {
        title: 'Governing Law',
        content: `These terms and your use of the Foodrolla platform are governed by the laws of the Netherlands. Any legal proceedings will be conducted in the competent courts of Amsterdam. If any part of these terms is found to be unenforceable, the remaining sections will continue to be in full force and effect.`
      },
      contactInfo: {
        title: 'Contact Information',
        content: `If you have questions about these terms or our privacy practices, please reach out to our legal department. Foodrolla Legal & Compliance Dept. Amsterdam, The Netherlands hello.foodrolla@gmail.com`
      }
    }
  },
  home: {
    heroTag: 'The Future of Procurement',
    heroTitle: 'Sourcing the Finest for Europe’s Best Kitchens.',
    heroSubtitle: 'Connecting Michelin-starred restaurants, artisan bakeries, and premium caterers directly with local soil and sea.',
    primaryCta: 'Explore Marketplace',
    secondaryCta: 'Join as Supplier',
    stats: {
      suppliers: 'Verified Producers',
      products: 'Premium Ingredients',
      delivery: '24h Logistics'
    },
    logos: {
      title: 'Trusted by Europe’s leading establishments'
    },
    categories: {
      title: 'Curated Categories',
      subtitle: 'Browse through our hand-picked selection of regional excellence.',
      produce: 'Field & Orchard',
      dairy: 'Creamery & Cheese',
      meat: 'Pasture & Provenance',
      sea: 'Coastal Catch',
      bakery: 'Artisan Grain',
      wine: 'Vineyard & Cellar'
    },
    products: {
      title: 'Seasonal Highlights',
      subtitle: 'Directly from the source to your kitchen.',
      viewAll: 'View Collection'
    },
    howItWorks: {
      title: 'How Foodrolla Works',
      step1Title: 'Discover Producers',
      step1Desc: 'Browse thousands of verified local suppliers and regional wholesalers.',
      step2Title: 'Direct Ordering',
      step2Desc: 'Place orders directly with multiple suppliers through one unified interface.',
      step3Title: 'Unified Logistics',
      step3Desc: 'Receive your premium ingredients in one consolidated, temperature-controlled delivery.'
    },
    trust: {
      verified: 'Verified Suppliers',
      verifiedDesc: 'Every producer is manually vetted for quality and safety.',
      delivery: '24h Delivery',
      deliveryDesc: 'Consolidated logistics for maximum freshness.',
      secure: 'Secure Payments',
      secureDesc: 'B2B payment terms and secure transactions.'
    },
    story: {
      title: 'From Soil to Service',
      description: 'We believe the best ingredients shouldn’t travel through endless middlemen. Foodrolla shortens the distance between the producer’s hand and the chef’s knife.',
      cta: 'Our Philosophy'
    },
    suppliers: {
      title: 'Featured Producers',
      subtitle: 'The masters behind the ingredients.',
      products: 'Products',
      rating: 'Rating',
      viewAll: 'View All Partners'
    },
    testimonials: {
      title: 'Voices from the Kitchen',
      quote1: 'Foodrolla has completely transformed how we source our seasonal produce. The direct connection to farmers is visible on the plate.',
      author1: 'Chef Marc Aubergine',
      role1: 'Executive Chef, L’Etoile',
      quote2: 'Efficiency meets quality. One invoice, one delivery, but the world’s best ingredients. It is the future of our industry.',
      author2: 'Sophie Vandeveld',
      role2: 'Owner, The Artisan Bakery'
    },
    cta: {
      title: 'Ready to redefine your supply chain?',
      primary: 'Get Started',
      secondary: 'Talk to Sales'
    }
  },
  marketplace: {
    tag: 'B2B COLLECTION',
    title: 'Source the Finest Ingredients',
    subtitle: 'Direct access to producers and artisan farms across Europe.',
    searchPlaceholder: 'Search for premium truffles, artisan cheese, or local farms...',
    filters: 'Filters',
    allIngredients: 'All Ingredients',
    priceLabel: 'B2B Price',
    noResults: 'No results found',
    noResultsDesc: 'Try adjusting your search or category filters.',
    loadingDesc: 'Curating the marketplace...',
    directSource: 'Direct Source'
  },
  product: {
    back: 'Back to Marketplace',
    quality: 'Quality',
    vetted: 'Vetted',
    delivery: 'Delivery',
    express: '24h Express',
    stock: 'Stock',
    by: 'By',
    orderQuantity: 'Order Quantity',
    totalAmount: 'Total Amount',
    addToOrder: 'Add to Order',
    minOrderNote: 'Minimum order requirements may apply for regional delivery.',
    producerBackground: 'Producer Background',
    viewProfile: 'View Producer Profile',
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Enter your credentials to access your account.',
    email: 'Email address',
    password: 'Password',
    loginButton: 'Sign in',
    noAccount: "Don't have an account?",
    signUpLink: 'Sign up',
    signupTitle: 'Create Supplier Account',
    signupSubtitle: 'Join our network of premium producers',
    register: 'Register',
    alreadyAccount: 'Already have an account? Sign in'
  },
  dashboard: {
    title: 'Supplier Overview',
    welcome: 'Welcome back to your supplier portal',
    revenue: 'Total Revenue',
    orders: 'Orders',
    products: 'Products',
    insights: 'Market Insights',
    topCategory: 'Top Selling Category',
    avgOrders: 'Average Daily Orders',
    launchPhase: 'Launch Phase',
    helpTitle: 'Need help?',
    helpDesc: 'Our support team is here to assist with your listings.',
    contactSupport: 'Contact Support',
    recentOrders: 'Recent Orders',
    orderId: 'Order ID',
    customer: 'Customer',
    date: 'Date',
    amount: 'Amount',
    status: 'Status',
    statusPending: 'Pending',
    statusCompleted: 'Completed',
    statusProcessing: 'Processing',
    productMgmt: 'Product Management',
    productMgmtDesc: 'Manage your catalog, stock and pricing',
    addProduct: 'Add Product',
    editProduct: 'Edit Product',
    deleteConfirm: 'Are you sure you want to delete this product?',
    available: 'Available',
    outOfStock: 'Out of Stock',
    price: 'Price',
    stockQty: 'Stock',
    incomingOrders: 'Incoming Orders',
    incomingOrdersDesc: 'Manage your active fulfillment and order history',
    noOrders: 'No orders yet',
    noOrdersDesc: 'Once buyers purchase your products, they will appear here.',
    from: 'From',
    individualBuyer: 'Individual Buyer',
    settings: 'Settings',
    profileDesc: 'Manage your business information and public profile',
    businessName: 'Business Name',
    location: 'Location',
    logoUrl: 'Logo URL',
    changeImage: 'Change Image',
    verifiedPartner: 'Verified Partner',
    verificationPending: 'Verification Pending',
    businessDesc: 'Business Description',
    businessDescPlaceholder: 'Tell buyers about your farm, products, and values...',
    profileUpdated: 'Profile updated successfully!',
  }
};